import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { SessionService } from '@core/services/session.service';
import { ConfigService } from '@core/services/config.service';
import { trigger, transition, query, style, stagger, animate } from '@angular/animations';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { StorageKey } from '@core/config/global.config';
import { UserService } from '@core/services/user.service';
import { DisplayDensity, DisplayDensityService } from '@layout/services/display-density.service';
import { Theme, ThemeService } from '@layout/services/theme.service';
import { MenuPosition, MenuPositionService } from '@layout/services/menu-position.service';
import { environment } from 'src/environments/environment';
import { UserThumbsComponent } from '../../shared/components/user-thumbs.component';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { UpperCasePipe } from '@angular/common';
import { PrimeNG } from 'primeng/config';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

const listAnimation = trigger('listAnimation', [
  transition('* <=> *', [
    query(
      ':enter',
      [style({ opacity: 0 }), stagger('60ms', animate('600ms ease-out', style({ opacity: 1 })))],
      {
        optional: true,
      },
    ),
    query(':leave', animate('200ms', style({ opacity: 0 })), {
      optional: true,
    }),
  ]),
]);

@Component({
    selector: 'va-user-overlay',
    template: `
    <p-popover #userDetails showCloseIcon="false" [style]="overlayUserStyle">
      <div class="user-header">
        <va-user-thumbs class="user-image" [firstname]="userInfo?.firstName" [lastname]="userInfo?.lastName">
        </va-user-thumbs>
        <div class="user-information">
          <h6 class="user-title">{{ userInfo?.firstName }} {{ userInfo?.lastName }}</h6>
          <p class="user-subtitle">{{ userInfo?.email }}</p>
        </div>
      </div>
      @if (multilanguage) {
        <div class="user-language">
          <label for="lang">{{ 'LANGUAGE.TITLE' | translate }}</label>
          <p-select-button
            [options]="langOptions"
            [(ngModel)]="lang"
            (ngModelChange)="updatedLang($event)"
          ></p-select-button>
        </div>
      }
      <div class="user-density">
        <label for="density">{{ 'DISPLAY_DENSITY.TITLE' | translate }}</label>
        <p-select-button
          size="small"
          [options]="densityOptions"
          [(ngModel)]="density"
          (ngModelChange)="updatedDensity($event)"
        >
          <ng-template let-option #item>
            <span class="density-icon" [class]="'density-icon-' + option.value" [pTooltip]="option.label" tooltipPosition="top"></span>
          </ng-template>
        </p-select-button>
      </div>
      <div class="user-density">
        <label for="theme">{{ 'THEME.TITLE' | translate }}</label>
        <p-select-button
          size="small"
          [options]="themeOptions"
          [(ngModel)]="theme"
          (ngModelChange)="updatedTheme($event)"
        >
          <ng-template let-option #item>
            <i class="pi" [class]="option.icon" [pTooltip]="option.label" tooltipPosition="top"></i>
          </ng-template>
        </p-select-button>
      </div>
      <div class="user-density">
        <label for="menu-position">{{ 'MENU_POSITION.TITLE' | translate }}</label>
        <p-select-button
          size="small"
          [options]="menuPositionOptions"
          [(ngModel)]="menuPosition"
          (ngModelChange)="updatedMenuPosition($event)"
        >
          <ng-template let-option #item>
            <span class="menu-position-icon" [class]="'menu-position-icon-' + option.value" [pTooltip]="option.label" tooltipPosition="top"></span>
          </ng-template>
        </p-select-button>
      </div>
      <div class="user-menu">
        <li class="user-link" [routerLink]="['user-profile', 'settings']">
          <i class="pi pi-cog mr-2 user-icon"></i>
          {{ 'PAGES.USER.MENU.SETTINGS' | translate | uppercase }}
        </li>
        @if (notProduction) {
          <li class="user-link" [routerLink]="['user-profile', 'release-notes']">
            <i class="pi pi-book mr-2 user-icon"></i>
            {{ 'PAGES.USER.MENU.RELEASE_NOTES' | translate | uppercase }}
          </li>
        }
        <li class="user-link" (click)="logout()">
          <i class="pi pi-sign-out mr-2 user-icon"></i>
          {{ 'PAGES.LOGIN.LOGOUT.TITLE' | translate | uppercase }}
        </li>
      </div>
    </p-popover>
    `,
    animations: [listAnimation],
    standalone: true,
    imports: [
        PopoverModule,
        UserThumbsComponent,
        SelectButtonModule,
        FormsModule,
        RouterLink,
        UpperCasePipe,
        TranslateModule,
        TooltipModule,
    ],
})
export class UserOverlayComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private userService = inject(UserService);
  private primeNGConfig = inject(PrimeNG);
  private configService = inject(ConfigService);
  private translateService = inject(TranslateService);
  private displayDensityService = inject(DisplayDensityService);
  private themeService = inject(ThemeService);
  private menuPositionService = inject(MenuPositionService);

  @ViewChild('userDetails') userDetails: Popover;
  @Input() userInfo: any = null;

  notProduction = !environment.production;
  multilanguage: boolean = false;
  subscription: Subscription;
  private densitySubscription: Subscription;
  private themeSubscription: Subscription;
  private menuPositionSubscription: Subscription;
  overlayUserStyle = {
    backgroundColor: 'var(--surface-0)',
    color: 'var(--dark-blue-50)',
    width: '250px',
    margin: 'auto',
  };

  lang = this.sessionService.getItem(StorageKey.language)
    ? this.sessionService.getItem(StorageKey.language)
    : this.translateService.currentLang;

  langOptions: any[] = [
    { value: 'it', label: this.translateService.instant('LANGUAGE.IT') },
    { value: 'en', label: this.translateService.instant('LANGUAGE.EN') },
  ];

  density: DisplayDensity = 'comfortable';
  densityOptions: any[] = [
    { value: 'compact', label: this.translateService.instant('DISPLAY_DENSITY.COMPACT') },
    { value: 'comfortable', label: this.translateService.instant('DISPLAY_DENSITY.COMFORTABLE') },
    { value: 'wide', label: this.translateService.instant('DISPLAY_DENSITY.WIDE') },
  ];

  theme: Theme = 'light';
  themeOptions: any[] = [
    { value: 'light', icon: 'pi-sun', label: this.translateService.instant('THEME.LIGHT') },
    { value: 'dark', icon: 'pi-moon', label: this.translateService.instant('THEME.DARK') },
  ];

  menuPosition: MenuPosition = 'top';
  menuPositionOptions: any[] = [
    { value: 'top', label: this.translateService.instant('MENU_POSITION.TOP') },
    { value: 'left', label: this.translateService.instant('MENU_POSITION.LEFT') },
  ];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit(): void {
    this.subscription = this.configService.getConfig().subscribe((res) => {
      this.multilanguage = res.language.multilanguage;
    });

    this.densitySubscription = this.displayDensityService.getDensity().subscribe((density) => {
      this.density = density;
    });

    this.themeSubscription = this.themeService.getTheme().subscribe((theme) => {
      this.theme = theme;
    });

    this.menuPositionSubscription = this.menuPositionService.getMenuPosition().subscribe((menuPosition) => {
      this.menuPosition = menuPosition;
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.densitySubscription.unsubscribe();
    this.themeSubscription.unsubscribe();
    this.menuPositionSubscription.unsubscribe();
  }

  updatedLang(lang: string): void {
    this.sessionService.setItem(StorageKey.language, lang);
    this.translateService.setDefaultLang(lang);
    this.translateService.use(lang);
    this.translateService.get('PRIMENG').subscribe((res) => this.primeNGConfig.setTranslation(res));
  }

  updatedDensity(density: DisplayDensity): void {
    this.displayDensityService.setDensity(density);
  }

  updatedTheme(theme: Theme): void {
    this.themeService.setTheme(theme);
  }

  updatedMenuPosition(menuPosition: MenuPosition): void {
    this.menuPositionService.setMenuPosition(menuPosition);
  }

  logout(): void {
    this.userService.logout();
    this.router.navigate(['auth', 'tenant', this.sessionService.getItem('appId')]);
  }
}
